import axiosHttpClient from '#helpers/axiosHttpClient.js'
import handleApiError from '#helpers/logging/handleApiError.js'
import Logger from '#helpers/logging/Logger.js'
import { url_settings } from '#modules/settings/constants/endpoints.js'
import { defaultExpiresTime, errorRetryTime, nowTime } from '#stores/storeDefaults.js'
import { useAuthorizationStore } from '#stores/useAuthStore.js'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'

export const useSettingsStore = defineStore('settings', () => {
	const settings = useStorage('settings', {}, sessionStorage)
	const loading = shallowRef(false)
	const expires = useStorage('settings-expires', null, sessionStorage)
	let fetchPromise = null

	/**
	 * Get a specific setting for a specific module.
	 * @param {string} module - The module to look into
	 * @param {string} setting - The specific setting we are looking for
	 * @returns {unknown} - The value of this setting for this module if it exists
	 */
	function get(module, setting) {
		fetchSettings()

		if (module !== null && setting !== null) {
			return settings.value?.[module]?.[setting]
		} else if (module !== null) {
			return settings.value?.[module]
		} else {
			return settings.value
		}
	}

	const list = computed(() => {
		fetchSettings()
		return settings.value
	})

	/**
	 * Fetch all the settings.
	 * @param {boolean} [force] - Whether to force fetching the settings
	 * @returns {Promise} - A promise to act upon
	 */
	async function fetchSettings(force = false) {
		// if the request is already in flight, return that promise
		if (!force && fetchPromise) {
			return fetchPromise
		}
		// if the data has not yet expired and we're not forcing
		if (!force && new Date().getTime() < expires.value) {
			return
		}

		// if the user is not logged in
		const auth = useAuthorizationStore()
		if (!auth.accessToken) {
			return
		}

		loading.value = true

		fetchPromise = axiosHttpClient
			.get(url_settings)
			.then(response => {
				const data = response?.data?.data ?? []
				settings.value = data.reduce((acc, currentSetting) => {
					acc[currentSetting.module] ??= {}
					acc[currentSetting.module][currentSetting.key] = currentSetting.value
					return acc
				}, {})
				expires.value = nowTime() + defaultExpiresTime
				return
			})
			.catch(e => {
				Logger.debug('useSettingsStore: fetch failed')
				expires.value = nowTime() + errorRetryTime
				handleApiError(e)
			})
			.finally(() => {
				loading.value = false
				fetchPromise = null
			})

		return fetchPromise
	}

	/**
	 * Clean up the data.
	 */
	function remove() {
		loading.value = false
		settings.value = []
		expires.value = 0
	}

	return { settings, loading, expires, get, list, fetch: fetchSettings, remove }
})
